import { type CSSProperties, useState, useRef, type ReactElement, type UIEvent } from 'react';

import styles from './index-ranking-distribution.module.scss';
import { pIdToCloudinary } from '@utmb/utils/cloudinary';
import { Image } from '@utmb/design-system/image/Image';
import { Placeholder } from '@utmb/design-system/placeholder/Placeholder';
import RunnerStatisticIndexDistributionIntervalView from '@utmb/types/view/RunnerStatisticIndexDistributionIntervalView';
import { PiCategoryLogo } from '@utmb/design-system/pi-category-logo/PiCategoryLogo';
import { PiCategoryEnum } from '@utmb/types/enum/PiCategoryEnum';
import { Font } from '@utmb/design-system/text/font';
import { getCategoryColor, getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { useTranslation } from 'next-i18next';

export interface IndexRankingDistributionProps {
    distribution: RunnerStatisticIndexDistributionIntervalView[];
    profilePicture: string | null;
    fullname?: string;
    category?: PiCategoryEnum;
    runnerIndex?: number | null;
}

const SLOT_WIDTH = 8;
const TICK_STEP = 100;

export function IndexRankingDistribution({
    distribution,
    profilePicture,
    fullname = '',
    category = PiCategoryEnum.GENERAL,
    runnerIndex = null,
}: IndexRankingDistributionProps): ReactElement {
    const { t } = useTranslation();
    const maxTotal = Math.max(...distribution.map((d) => d.total), 1);
    const sortedDistribution = [...distribution].sort((a, b) => Math.min(a.indexMin, a.indexMax) - Math.min(b.indexMin, b.indexMax));
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [scrollTop, setScrollTop] = useState(0);
    const scrollWrapRef = useRef<HTMLDivElement>(null);

    const handleScroll = (e: UIEvent<HTMLDivElement>): void => {
        setScrollLeft(e.currentTarget.scrollLeft);
        setScrollTop(e.currentTarget.scrollTop);
    };

    const hoveredInterval = hoveredIndex !== null ? sortedDistribution[hoveredIndex] : null;
    const categoryColor = getCategoryColor(category);

    const tooltipBoundaryClass: string = (() => {
        if (!hoveredInterval) return '';
        const lo = Math.min(hoveredInterval.indexMin, hoveredInterval.indexMax);
        const hi = Math.max(hoveredInterval.indexMin, hoveredInterval.indexMax);
        if (hi < 100) return styles.boundaryStart;
        if (lo > 900) return styles.boundaryEnd;
        return '';
    })();

    const ticks: { value: number; position: number }[] = [];
    if (sortedDistribution.length > 0) {
        const chartLo = Math.min(sortedDistribution[0].indexMin, sortedDistribution[0].indexMax);
        const leftEdgeValue = chartLo - 1;
        if (leftEdgeValue >= 0 && leftEdgeValue % TICK_STEP === 0) {
            ticks.push({ value: leftEdgeValue, position: 0 });
        }
        sortedDistribution.forEach((it, idx) => {
            const hi = Math.max(it.indexMin, it.indexMax);
            if (hi % TICK_STEP === 0) {
                ticks.push({ value: hi, position: (idx + 1) * SLOT_WIDTH });
            }
        });
    }

    const axisSize = sortedDistribution.length * SLOT_WIDTH;
    const hoverBarCenter = hoveredIndex !== null ? hoveredIndex * SLOT_WIDTH + SLOT_WIDTH / 2 : 0;

    return (
        <div className={styles.indexRankingDistribution}>
            {hoveredInterval && hoveredIndex !== null && (
                <div
                    className={`${styles.wrapTooltip} ${tooltipBoundaryClass}`}
                    style={
                        {
                            ['--hover-pos-x' as string]: `${hoverBarCenter - scrollLeft}px`,
                            ['--hover-pos-y' as string]: `${hoverBarCenter - scrollTop}px`,
                        } as CSSProperties
                    }
                >
                    <div className={styles.tooltip}>
                        <div className={styles.wrapIndex}>
                            <PiCategoryLogo height={20} piCategory={category} isDark={false} />
                            <Font.OxaniumBold desktop="16" mobile="16" color={getColorStyle(ColorTypesEnum.WS_PRIMARY)}>
                                {Math.min(hoveredInterval.indexMin, hoveredInterval.indexMax)} - {Math.max(hoveredInterval.indexMin, hoveredInterval.indexMax)}
                            </Font.OxaniumBold>
                        </div>
                        <Font.FuturaBook className={styles.value} desktop="16" mobile="16" color={getColorStyle(ColorTypesEnum.GREY_TEXT)}>
                            {hoveredInterval.total} {t('singleRunner.statistics.runner')}
                        </Font.FuturaBook>
                    </div>
                </div>
            )}

            <div ref={scrollWrapRef} className={styles.scrollWrap} onScroll={handleScroll}>
                <div className={styles.barChart} style={{ ['--axis-size' as string]: `${axisSize}px` } as CSSProperties}>
                    {sortedDistribution.map((interval, idx) => {
                        const heightPct = (interval.total / maxTotal) * 100;
                        const isHighlight = interval.hasRunner;
                        const wrapBarClassName = isHighlight ? `${styles.wrapBar} ${styles.highlight}` : styles.wrapBar;

                        return (
                            <div
                                key={`${interval.indexMin}-${interval.indexMax}`}
                                className={wrapBarClassName}
                                onMouseEnter={() => setHoveredIndex(idx)}
                                onMouseLeave={() => setHoveredIndex(null)}
                            >
                                <div
                                    className={styles.bar}
                                    style={
                                        {
                                            ['--bar-pct' as string]: `${heightPct}%`,
                                            ['--bar-min' as string]: interval.total > 0 ? '1px' : '0',
                                            ['--bar-bg' as string]: categoryColor,
                                        } as CSSProperties
                                    }
                                />
                                {isHighlight && (
                                    <>
                                        <div className={styles.connector} />
                                        <div className={styles.avatar}>
                                            {profilePicture ? (
                                                <Image objectFit="cover" layout="fill" image={pIdToCloudinary(profilePicture, fullname)} />
                                            ) : (
                                                <Placeholder className={styles.placeholder} isInvert logoWidth={30} logoHeight={30} />
                                            )}
                                        </div>
                                        {runnerIndex !== null && (
                                            <div className={styles.runnerIndexWrap}>
                                                <PiCategoryLogo height={20} piCategory={category} isDark={true} />
                                                <Font.OxaniumMedium
                                                    className={styles.runnerIndex}
                                                    desktop="26"
                                                    mobile="20"
                                                    color={getColorStyle(ColorTypesEnum.WHITE)}
                                                >
                                                    {runnerIndex}
                                                </Font.OxaniumMedium>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div className={styles.axis} style={{ ['--axis-size' as string]: `${axisSize}px` } as CSSProperties}>
                    {ticks.map(({ value, position }, i) => {
                        const isFirst = i === 0;
                        const isLast = i === ticks.length - 1;
                        const edgeClass = isFirst ? styles.tickFirst : isLast ? styles.tickLast : '';
                        return (
                            <div key={value} className={`${styles.tick} ${edgeClass}`} style={{ ['--tick-pos' as string]: `${position}px` } as CSSProperties}>
                                <span className={styles.tickMark} />
                                <div className={styles.tickLabel}>
                                    <Font.OxaniumBold desktop="16" mobile="16" color={getColorStyle(ColorTypesEnum.WHITE)}>
                                        {value}
                                    </Font.OxaniumBold>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
